/**
 * PM Training Clip 7 — Competition Settings & Overlay Styles
 * Setup page: change scoring model, toggle overlay defaults, preview each style.
 * 1200 frames at 30fps = 40 seconds.
 *
 * Flow:
 *  1. Title
 *  2. Setup page — Competition Settings card highlighted
 *  3. Change scoring model dropdown to "Average"
 *  4. Toggle overlay defaults checkboxes
 *  5. After toggling, show OBS preview modal for 2 seconds
 *  6. Change overlay style — show each style's OBS preview (5 styles × ~3s)
 *  7. Done
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { AutoCursor } from "../components/AutoCursor";
import { ScreenMock } from "../components/ScreenMock";
import { FadeTransition } from "../components/FadeTransition";
import { StepLabel } from "../components/StepLabel";

const M = "'JetBrains Mono', monospace";
const ACCENT = "#a855f7";

export function PMCourse7() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={65}><Title /></Sequence>
      <Sequence from={55} durationInFrames={1160}>
        <FadeTransition durationInFrames={1160} fadeIn={10} fadeOut={10}><Demo /></FadeTransition>
      </Sequence>
      <Sequence from={1210} durationInFrames={250}>
        <FadeTransition durationInFrames={250} fadeIn={10} fadeOut={10}><StyleShowcase /></FadeTransition>
      </Sequence>
      <Sequence from={1455} durationInFrames={65}><Done /></Sequence>
    </AbsoluteFill>
  );
}

function Title() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 48, 65], [0, 1, 1, 0], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ fontSize: 10, color: ACCENT, textTransform: "uppercase", letterSpacing: 3, fontFamily: M, marginBottom: 14 }}>Competition Manager Training · Clip 7</div>
      <div style={{ fontSize: 48, fontWeight: 800, color: "#fff" }}>Settings & Overlay Styles</div>
    </AbsoluteFill>
  );
}

/*
 * Timeline (frames relative to Demo):
 *   0-150:    Settings card highlighted, point at scoring model
 *   150-300:  Change scoring model to Average
 *   300-500:  Toggle overlay defaults
 *   500-600:  OBS preview modal showing the result
 *   600-900:  Change overlay style dropdown, cycle through styles
 */

function Demo() {
  const frame = useCurrentFrame();

  /*
   * Timeline:
   *   0-120:     Settings card highlighted
   *   120-250:   Scoring model — click dropdown, show options, select Average
   *   250-360:   Toggle banner OFF → OBS preview updates (2s)
   *   360-470:   Toggle banner ON, toggle riders ON → OBS preview updates (2s)
   *   470-580:   Toggle scores ON → OBS preview updates (2s)
   *   580-690:   Toggle leaderboard ON → OBS preview updates (2s)
   *   690-900:   Cycle overlay styles with OBS preview
   */

  /*
   * Timeline:
   *   0-120:     Settings card highlighted
   *   120-250:   Scoring model — click dropdown, show options, select Average
   *   250-370:   Toggle banner OFF → preview without banner (2s)
   *   370-420:   Toggle banner back ON
   *   420-530:   Toggle riders OFF → preview without riders (2s)
   *   530-580:   Toggle riders back ON
   *   580-690:   Toggle scores ON → preview with scores (2s)
   *   690-800:   Toggle leaderboard ON → preview with leaderboard (2s)
   *   800-900:   Cycle overlay styles with preview
   */

  const scoringModel = frame < 180 ? "max" : "average";
  const showScoringDropdown = frame >= 150 && frame < 200;

  const checkboxes = {
    banner: !(frame >= 280 && frame < 390),
    riders: !(frame >= 450 && frame < 550),
    scores: frame >= 610,
    leaderboard: frame >= 720,
  };
  const overlayStyle = frame < 840 ? "steel" : frame < 920 ? "olympic" : frame < 1000 ? "broadcast" : frame < 1080 ? "neon" : "clean";
  const showObsPreview = frame >= 280;
  const showStyleDropdown = frame >= 810 && frame < 850;

  const section =
    frame < 120 ? "settings" :
    frame < 250 ? "scoring" :
    frame < 800 ? "defaults" :
    "styles";

  return (
    <AbsoluteFill>
      <ScreenMock url="raley.koda-software.com/competition/moomba/setup">
        <div style={shell}>
          <div style={topbar}>
            <span style={brand}>RALEY</span>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#e4e4e7" }}>Competitions</span>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#71717a" }}>Riders</span>
            <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#71717a" }}>Training</span>
            <span style={{ marginLeft: "auto", fontFamily: M, fontSize: 11, color: "#71717a" }}>admin@koda-software.com</span>
          </div>

          <div style={{ padding: "16px 24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, overflow: "hidden" }}>
            {/* Left column — Divisions + Heats (dimmed) */}
            <div style={{ opacity: 0.3 }}>
              <div style={{ background: "#111113", border: "1px solid #2a2a32", padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>Divisions</div>
                <div style={{ fontSize: 11, color: "#71717a" }}>Men&apos;s Open, Women&apos;s Open</div>
              </div>
              <div style={{ background: "#111113", border: "1px solid #2a2a32", padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>Heats</div>
                <div style={{ fontSize: 11, color: "#71717a" }}>Round 1, Semi-Finals, Finals</div>
              </div>
            </div>

            {/* Right column — Competition Settings */}
            <div>
              <div id="settings-card" style={{ background: "#111113", border: "1px solid #2a2a32", padding: 16, ...glow(section === "settings") }}>
                <div style={{ fontSize: 14, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 16 }}>Competition Settings</div>

                {/* Scoring Model */}
                <div id="scoring-section" style={{ marginBottom: 16, padding: 6, border: "1px solid transparent", ...glow(section === "scoring") }}>
                  <div style={fieldLabel}>Scoring Model</div>
                  <div style={{ position: "relative" }}>
                    <div id="scoring-dropdown" style={{
                      padding: "8px 12px", background: "#0a0a0b", border: "1px solid #2a2a32",
                      fontSize: 13, color: "#e4e4e7", display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                      <span>{scoringModel === "max" ? "Best Score (Max)" : "Average"}</span>
                      <span style={{ fontSize: 10, color: "#52525b" }}>&#9662;</span>
                    </div>
                    {showScoringDropdown && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#0a0a0b", border: "1px solid #2a2a32", zIndex: 20 }}>
                        <div id="dd-max" style={{ padding: "8px 12px", fontSize: 12, color: scoringModel === "max" ? "#3b82f6" : "#e4e4e7", background: scoringModel === "max" ? "rgba(59,130,246,0.1)" : "transparent", fontWeight: scoringModel === "max" ? 700 : 400 }}>Best Score (Max)</div>
                        <div id="dd-average" style={{ padding: "8px 12px", fontSize: 12, color: scoringModel !== "max" ? "#3b82f6" : "#e4e4e7", background: scoringModel !== "max" ? "rgba(59,130,246,0.1)" : "transparent", fontWeight: scoringModel !== "max" ? 700 : 400 }}>Average</div>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#71717a", marginTop: 6 }}>
                    {scoringModel === "max"
                      ? "Rider keeps only their highest attempt score"
                      : "Average of all attempt scores"}
                  </div>
                  <div style={{ fontSize: 11, color: "#52525b", marginTop: 4 }}>Judges: 3 (set at creation, cannot be changed)</div>
                </div>

                {/* Overlay Defaults */}
                <div id="defaults-section" style={{ marginBottom: 16, padding: 6, border: "1px solid transparent", ...glow(section === "defaults") }}>
                  <div style={fieldLabel}>Overlay Defaults (applied on Go Live)</div>
                  {[
                    { key: "banner", label: "Show heat name banner", checked: checkboxes.banner },
                    { key: "riders", label: "Show heat riders", checked: checkboxes.riders },
                    { key: "scores", label: "Show rider scores", checked: checkboxes.scores },
                    { key: "leaderboard", label: "Show leaderboard", checked: checkboxes.leaderboard },
                  ].map(({ key, label, checked }) => (
                    <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer", padding: "4px 0" }}>
                      <div id={`cb-${key}`} style={{
                        width: 14, height: 14, border: `1.5px solid ${checked ? "#3b82f6" : "#52525b"}`,
                        background: checked ? "#3b82f6" : "transparent", borderRadius: 2,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 9, color: "#fff", fontWeight: 700,
                      }}>
                        {checked ? "✓" : ""}
                      </div>
                      <span style={{ color: "#e4e4e7" }}>{label}</span>
                    </label>
                  ))}
                </div>

                {/* Default Overlay Style */}
                <div id="style-section" style={{ padding: 6, border: "1px solid transparent", ...glow(section === "styles") }}>
                  <div style={fieldLabel}>Default Overlay Style</div>
                  <div style={{ position: "relative" }}>
                    <div id="style-dropdown" style={{
                      padding: "8px 12px", background: "#0a0a0b", border: "1px solid #2a2a32",
                      fontSize: 13, color: "#e4e4e7", display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}>
                      <span>{overlayStyle.charAt(0).toUpperCase() + overlayStyle.slice(1)}</span>
                      <span style={{ fontSize: 10, color: "#52525b" }}>&#9662;</span>
                    </div>
                    {showStyleDropdown && (
                      <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#0a0a0b", border: "1px solid #2a2a32", zIndex: 20 }}>
                        {["Steel", "Olympic", "Broadcast", "Neon", "Clean"].map((s) => {
                          const isCurrent = s.toLowerCase() === overlayStyle;
                          return (
                            <div key={s} id={`dd-style-${s.toLowerCase()}`} style={{ padding: "6px 12px", fontSize: 12, color: isCurrent ? "#3b82f6" : "#e4e4e7", background: isCurrent ? "rgba(59,130,246,0.1)" : "transparent", fontWeight: isCurrent ? 700 : 400 }}>{s}</div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Save button */}
                {(scoringModel === "average" || checkboxes.leaderboard) && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ display: "inline-flex", padding: "6px 14px", background: "#3b82f6", color: "#fff", fontSize: 11, fontWeight: 600 }}>Save Changes</div>
                  </div>
                )}
              </div>

              {/* Invite Team (dimmed) */}
              <div style={{ background: "#111113", border: "1px solid #2a2a32", padding: 16, marginTop: 16, opacity: 0.3 }}>
                <div style={{ fontSize: 14, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 }}>Invite Team</div>
              </div>
            </div>
          </div>
        </div>

        {/* OBS Preview — large, bottom-left */}
        {showObsPreview && (
          <div style={{
            position: "absolute", bottom: 10, left: 20,
            background: "#111113", border: `2px solid ${ACCENT}`, borderRadius: 8,
            padding: 12, width: 560, boxShadow: `0 0 0 2px ${ACCENT}, 0 0 20px rgba(168,85,247,0.2)`,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: ACCENT, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, fontFamily: M }}>OBS Overlay Preview</div>
              <div style={{ fontSize: 10, color: "#71717a", fontFamily: M }}>
                Style: <span style={{ color: "#e4e4e7", fontWeight: 600 }}>{overlayStyle.charAt(0).toUpperCase() + overlayStyle.slice(1)}</span>
              </div>
            </div>
            <ObsMiniPreview style={overlayStyle} showBanner={checkboxes.banner} showRiders={checkboxes.riders} showScores={checkboxes.scores} showLeaderboard={checkboxes.leaderboard} />
          </div>
        )}

        <AutoCursor points={[
          { x: 960, y: 400, frame: 0 },
          { x: 960, y: 400, frame: 30 },
          // Settings card
          { target: "settings-card", frame: 60 },
          { target: "settings-card", frame: 115 },
          // Scoring model — click dropdown
          { target: "scoring-dropdown", frame: 135 },
          { target: "scoring-dropdown", frame: 150, click: true },
          // Select Average from dropdown
          { target: "dd-average", frame: 165 },
          { target: "dd-average", frame: 180, click: true },
          { target: "scoring-dropdown", frame: 240 },
          // Toggle banner OFF — click the checkbox
          { target: "cb-banner", frame: 260 },
          { target: "cb-banner", frame: 280, click: true },
          // Watch OBS preview — banner gone
          { x: 300, y: 750, frame: 310 },
          { x: 300, y: 750, frame: 370 },
          // Toggle banner back ON
          { target: "cb-banner", frame: 380 },
          { target: "cb-banner", frame: 390, click: true },
          // Watch preview — banner is back
          { x: 300, y: 750, frame: 410 },
          // Toggle riders OFF
          { target: "cb-riders", frame: 430 },
          { target: "cb-riders", frame: 450, click: true },
          // Watch preview — riders gone
          { x: 300, y: 750, frame: 480 },
          { x: 300, y: 750, frame: 530 },
          // Toggle riders back ON
          { target: "cb-riders", frame: 540 },
          { target: "cb-riders", frame: 550, click: true },
          { x: 300, y: 750, frame: 570 },
          // Toggle scores ON
          { target: "cb-scores", frame: 590 },
          { target: "cb-scores", frame: 610, click: true },
          { x: 300, y: 750, frame: 640 },
          { x: 300, y: 750, frame: 680 },
          // Toggle leaderboard ON
          { target: "cb-leaderboard", frame: 700 },
          { target: "cb-leaderboard", frame: 720, click: true },
          { x: 300, y: 750, frame: 750 },
          { x: 300, y: 750, frame: 790 },
          // Cycle overlay styles — click dropdown, select each with pauses
          { target: "style-dropdown", frame: 800 },
          { target: "style-dropdown", frame: 815, click: true },
          { target: "dd-style-olympic", frame: 830 },
          { target: "dd-style-olympic", frame: 840, click: true },
          // Watch Olympic
          { x: 300, y: 750, frame: 870 },
          { x: 300, y: 750, frame: 910 },
          // Select Broadcast
          { target: "style-dropdown", frame: 915 },
          { target: "style-dropdown", frame: 920, click: true },
          { x: 300, y: 750, frame: 950 },
          { x: 300, y: 750, frame: 990 },
          // Select Neon
          { target: "style-dropdown", frame: 995 },
          { target: "style-dropdown", frame: 1000, click: true },
          { x: 300, y: 750, frame: 1030 },
          { x: 300, y: 750, frame: 1070 },
          // Select Clean
          { target: "style-dropdown", frame: 1075 },
          { target: "style-dropdown", frame: 1080, click: true },
          { x: 300, y: 750, frame: 1110 },
          { x: 300, y: 750, frame: 1150 },
        ]} />
      </ScreenMock>

      <StepLabel text="The Competition Settings card controls scoring and overlay behavior" show={10} hide={115} color={ACCENT} />
      <StepLabel text="Change the scoring model — select from the dropdown" show={120} hide={245} color="#3b82f6" />
      <StepLabel text="'Show heat name banner' OFF — the heat banner disappears from the overlay" show={250} hide={380} color="#3b82f6" />
      <StepLabel text="'Show heat riders' OFF — the rider bar at the bottom disappears" show={420} hide={545} color="#3b82f6" />
      <StepLabel text="'Show rider scores' ON — scores appear under each rider's name" show={580} hide={695} color="#22c55e" />
      <StepLabel text="'Show leaderboard' ON — full standings table appears on the overlay" show={700} hide={795} color="#22c55e" />
      <StepLabel text="Change the overlay style — each theme has a unique broadcast look" show={800} hide={1150} color={ACCENT} />
    </AbsoluteFill>
  );
}

/* ── Style Showcase: full-screen each style for ~3s ── */

const STYLES = [
  { name: "Steel", bg: "rgba(10,10,11,0.88)", border: "rgba(255,255,255,0.08)", text: "#fff", sub: "#52525b", chip: "rgba(10,10,11,0.85)", accent: "#3b82f6" },
  { name: "Olympic", bg: "rgba(0,40,80,0.92)", border: "rgba(100,180,255,0.2)", text: "#fff", sub: "rgba(180,220,255,0.6)", chip: "rgba(0,40,80,0.88)", accent: "#fbbf24" },
  { name: "Broadcast", bg: "rgba(220,38,38,0.95)", border: "rgba(255,255,255,0.2)", text: "#fff", sub: "rgba(255,255,255,0.7)", chip: "rgba(15,23,42,0.95)", accent: "#fbbf24" },
  { name: "Neon", bg: "rgba(0,0,0,0.9)", border: "rgba(34,211,238,0.3)", text: "#22d3ee", sub: "rgba(34,211,238,0.4)", chip: "rgba(0,0,0,0.88)", accent: "#f43f5e" },
  { name: "Clean", bg: "rgba(255,255,255,0.92)", border: "rgba(0,0,0,0.08)", text: "#0f172a", sub: "#94a3b8", chip: "rgba(255,255,255,0.9)", accent: "#2563eb" },
];

function StyleShowcase() {
  const frame = useCurrentFrame();
  const styleIdx = Math.min(Math.floor(frame / 50), STYLES.length - 1);
  const s = STYLES[styleIdx];
  const localFrame = frame - styleIdx * 50;
  const op = interpolate(localFrame, [0, 10, 40, 50], [0, 1, 1, 0], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ fontSize: 10, color: ACCENT, textTransform: "uppercase", letterSpacing: 3, fontFamily: M, marginBottom: 16 }}>Overlay Style</div>
      <div style={{ fontSize: 36, fontWeight: 800, color: "#fff", marginBottom: 32 }}>{s.name}</div>

      {/* Mini overlay mock at larger scale */}
      <div style={{ width: 960, height: 540, position: "relative", background: "#1a1a1e", borderRadius: 8, overflow: "hidden", border: "1px solid #2a2a32" }}>
        {/* Banner */}
        <div style={{ position: "absolute", top: 15, left: 20, display: "flex", alignItems: "center", gap: 8, background: s.bg, border: `1px solid ${s.border}`, padding: "6px 16px" }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444", boxShadow: "0 0 6px #ef4444" }} />
          <span style={{ fontSize: 12, fontWeight: 800, color: s.text, textTransform: "uppercase", letterSpacing: 1 }}>MEN&apos;S — SEMI-FINALS</span>
          <span style={{ fontSize: 9, color: s.sub, fontFamily: M }}>Boardstock</span>
        </div>

        {/* Rider chips */}
        <div style={{ position: "absolute", bottom: 20, left: 20, right: 20, display: "flex", gap: 8 }}>
          {["HC", "NR", "AW"].map((init, i) => (
            <div key={init} style={{ display: "flex", alignItems: "center", gap: 6, background: s.chip, border: `1px solid ${s.border}`, padding: "6px 12px" }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: ["#3b82f6", "#22c55e", "#f59e0b"][i], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#fff" }}>{init}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 11, color: s.text }}>{["Harley", "Nic", "Amber"][i]}</div>
                <div style={{ fontSize: 9, color: s.sub, fontFamily: M }}>{["8.5", "7.2", "9.0"][i]}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AbsoluteFill>
  );
}

function Done() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 24, color: "#fff" }}>&#10003;</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>Settings configured — your overlay is ready</div>
    </AbsoluteFill>
  );
}

/* ── Mini OBS Preview ── */

function ObsMiniPreview({ style: s, showBanner, showRiders, showScores, showLeaderboard }: { style: string; showBanner: boolean; showRiders: boolean; showScores: boolean; showLeaderboard: boolean }) {
  const colors = STYLES.find((st) => st.name.toLowerCase() === s) ?? STYLES[0];
  return (
    <div style={{ background: "#0a0a0b", border: "1px solid #2a2a32", height: 260, borderRadius: 4, position: "relative", overflow: "hidden" }}>
      {showBanner && (
        <div style={{ position: "absolute", top: 10, left: 12, display: "flex", alignItems: "center", gap: 8, background: colors.bg, border: `1px solid ${colors.border}`, padding: "5px 14px" }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#ef4444", boxShadow: "0 0 4px #ef4444" }} />
          <span style={{ fontSize: 9, fontWeight: 800, color: colors.text, textTransform: "uppercase", letterSpacing: 0.5, fontFamily: M }}>MEN&apos;S — SEMI-FINALS</span>
          <span style={{ fontSize: 7, color: colors.sub, fontFamily: M }}>Boardstock</span>
        </div>
      )}
      {showLeaderboard && (
        <div style={{ position: "absolute", left: "50%", top: "45%", transform: "translate(-50%, -50%)", background: colors.bg, border: `1px solid ${colors.border}`, padding: "10px 20px", minWidth: 280, textAlign: "center", opacity: 0.85 }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: colors.text, textTransform: "uppercase", marginBottom: 8, letterSpacing: 0.5, fontFamily: M }}>LEADERBOARD</div>
          {[{ n: "Amber Wing", s: "9.0" }, { n: "Harley Clifford", s: "8.5" }, { n: "Nic Rapa", s: "7.2" }].map((r, i) => (
            <div key={r.n} style={{ display: "flex", justifyContent: "space-between", padding: "3px 8px", fontSize: 8, fontFamily: M, color: i === 0 ? colors.accent : colors.sub }}>
              <span>{i + 1}. {r.n}</span><span style={{ fontWeight: 700 }}>{r.s}</span>
            </div>
          ))}
        </div>
      )}
      {showRiders && (
        <div style={{ position: "absolute", bottom: 12, left: 12, right: 12, display: "flex", gap: 6 }}>
          {[
            { init: "HC", name: "Harley", score: "8.5", color: "#3b82f6" },
            { init: "NR", name: "Nic", score: "7.2", color: "#22c55e" },
            { init: "AW", name: "Amber", score: "9.0", color: "#f59e0b" },
          ].map((r) => (
            <div key={r.init} style={{ display: "flex", alignItems: "center", gap: 6, background: colors.chip, border: `1px solid ${colors.border}`, padding: "5px 10px" }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", background: r.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 700, color: "#fff" }}>{r.init}</div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 8, color: colors.text }}>{r.name}</div>
                {showScores && <div style={{ fontSize: 7, color: colors.sub, fontFamily: M }}>{r.score}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Helpers ── */

function glow(active: boolean): React.CSSProperties {
  return active
    ? { boxShadow: "0 0 0 2px #3b82f6, 0 0 15px rgba(59,130,246,0.25)", borderColor: "#3b82f6" }
    : {};
}

const shell: React.CSSProperties = { width: "100%", height: "100%", background: "#0a0a0b", fontFamily: "'Inter', sans-serif", color: "#e4e4e7", display: "flex", flexDirection: "column" };
const topbar: React.CSSProperties = { height: 42, background: "#111113", borderBottom: "1px solid #2a2a32", display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0 };
const brand: React.CSSProperties = { fontFamily: M, fontWeight: 800, fontSize: 14, letterSpacing: 2 };
const fieldLabel: React.CSSProperties = { fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#71717a", marginBottom: 6, fontFamily: M };
